import {Material, Mesh, Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';

import React, {PropsWithoutRef, useEffect, useState} from 'react';

import {gameState} from '../state/GameState.js';

import {ads} from '../utils/uber-sdk/advertising.js';
import {showRewardedAd} from './functions.js';

import type {GameAnalytics as GA} from 'gameanalytics';
declare var GameAnalytics: typeof GA;

import {ThirdpersonCamera} from '../components/thirdperson-camera.js';

import {
    ReactUiBase,
    PositionType,
    FlexDirection,
    Justify,
    Align,
    YogaNodeProps,
} from '@wonderlandengine/react-ui';
import {
    Text,
    Column,
    Button,
    Row,
    Plane,
    ProgressBar,
    Panel,
    Container,
    MaterialContext,
} from '@wonderlandengine/react-ui/components';

import {ControlEvents} from '../components/control-events.js';
import {globalAudioManager} from '@wonderlandengine/spatial-audio';
import {Sounds} from '../utils/Audio.js';
import {Weapon} from '../state/combat/Weapon.js';
import {Constants} from '../state/data/constants.js';
import {isMobile} from '../utils/mobile.js';
import {ResourceFlowType} from '../utils/game-analytics-enums.js';
import {messageConsole} from '../components/message-controller.js';

declare const WITH_ADS: boolean;

const deathMessages = [
    "Don't give up! A powerful fruit awaits!",
    'Seize the juice! Power up and respawn!',
    'Harvest some courage and try again!',
    'Keep calm and berry on!',
    'Not ripe enough? Get better fruits!',
    "Life's a peach! Get back in with more fruit!",
];

class CoinDisplay extends React.Component<
    PropsWithoutRef<YogaNodeProps & {value: number}>
> {
    objRef = React.createRef<Object3D>();
    componentDidUpdate(prevProps: any) {
        if (this.props.value !== prevProps.value && this.objRef.current) {
            const o = this.objRef.current;
            const s = new Float32Array(3);
            o.getScalingLocal(s);
            const f = 1.3;
            setTimeout(() => o.setScalingLocal([s[0] * f, s[1] * f, s[2] * f]), 200);
            setTimeout(() => o.setScalingLocal(s), 2500);
        }
    }

    render() {
        return (
            <Text text={this.props.value.toString()} {...this.props} ref={this.objRef} />
        );
    }
}

const Color = {
    Background: 0x1d1d1dff,
    Green: 0x39ca00ff,
    Red: 0x960101ff,
    Blue: 0x0000ffff,
    Primary: 0x002561ff,
    PrimaryHovered: 0x005acaff,
};

const App = (props: {comp: ReactUi}) => {
    let infoScreenTimout: number;
    const [messages, setMessages] = useState([] as string[]);
    const [region, setRegion] = useState('');
    const [coins, setCoins] = useState(gameState.currentPlayer.coins);
    const [displayXp, setDisplayXp] = useState(gameState.currentPlayer.displayXp);
    const [level, setLevel] = useState(gameState.currentPlayer.level);
    const [playerHealth, setPlayerHealth] = useState(gameState.currentPlayer.health);
    const [playerMaxHealth, setPlayerMaxHealth] = useState(
        gameState.currentPlayer.maxHealth
    );
    const [playerEnergy, setPlayerEnergy] = useState(gameState.currentPlayer.energy);
    const [playerMaxEnergy, setPlayerMaxEnergy] = useState(
        gameState.currentPlayer.maxEnergy
    );
    useEffect(() => {
        const onChanged = (property: string) => {
            if (property === 'health') {
                setPlayerHealth(gameState.currentPlayer.health);
                return;
            }
            if (property === 'maxHealth') {
                setPlayerMaxHealth(gameState.currentPlayer.maxHealth);
                return;
            }
            if (property === 'energy') {
                setPlayerEnergy(Math.floor(gameState.currentPlayer.energy));
                return;
            }
            if (property === 'maxEnergy') {
                setPlayerMaxEnergy(gameState.currentPlayer.maxEnergy);
                return;
            }
            if (property === 'coins') {
                setCoins(gameState.currentPlayer.coins);
                return;
            }
            if (property === 'xp') {
                setDisplayXp(gameState.currentPlayer.displayXp);
                return;
            }
            if (property === 'level') {
                setLevel(gameState.currentPlayer.level);
                return;
            }
            if (property === 'currentRegion') {
                setRegion(gameState.currentPlayer.currentRegion);
                return;
            }
            if (property === 'isInShop') {
                // only change when player leaves the shop, entering is handled though
                // the onEnterShop event
                if (!gameState.currentPlayer.isInShop) {
                    setShowShop(false);
                    GameAnalytics.addDesignEvent('hud:shop:hide');
                }
                return;
            }
        };

        const onMessage = (property: string) => {
            if (property !== 'messages') {
                return;
            }
            /* Display last 5 messages */
            let messages = messageConsole.messages;
            setMessages(Array.from(messages.map((m) => m.content).slice(-5)));
        };

        messageConsole.propertyChanged.add(onMessage);
        gameState.currentPlayer.propertyChanged.add(onChanged);

        return () => {
            gameState.currentPlayer.propertyChanged.remove(onChanged);
            messageConsole.propertyChanged.remove(onMessage);
        };
    }, []);

    const [hasRewardedAd, setHasRewardedAd] = useState(false);
    useEffect(() => {
        const to = setTimeout(() => {
            setHasRewardedAd(true);
        }, 15000 /* 5 seconds after info screen disappears */);
        return () => clearTimeout(to);
    }, [hasRewardedAd]);

    const [showRespawnScreen, setShowRespawnScreen] = useState(
        gameState.currentPlayer.dead
    );
    const [deathMessage, setDeathMessage] = useState('');
    useEffect(() => {
        const onDie = () => {
            setShowRespawnScreen(true);
            setRespawnTimer(WITH_ADS ? Constants.respawnTimer : 3);
            if (WITH_ADS) {
                setDeathMessage('Watch and ad or respawn.');
            } else {
                setDeathMessage(
                    deathMessages[Math.floor(Math.random() * deathMessages.length)]
                );
            }
        };
        gameState.currentPlayer.onDie.add(onDie);
        return () => {
            gameState.currentPlayer.onDie.remove(onDie);
        };
    }, []);
    const [respawnTimer, setRespawnTimer] = useState(Constants.respawnTimer);
    useEffect(() => {
        const i = setInterval(() => {
            if (respawnTimer <= 1) clearInterval(i);
            setRespawnTimer(respawnTimer - 1);
        }, 1000);
        return () => clearInterval(i);
    }, [respawnTimer]);
    const [showInfoScreen, setShowInfoScreen] = useState(!isMobile);
    useEffect(() => {
        infoScreenTimout = setTimeout(() => {
            setShowInfoScreen(false);
        }, 10000);
        return () => clearTimeout(infoScreenTimout);
    }, [showInfoScreen]);

    const [shopItems, setShopItems] = useState([] as Weapon[]);
    const [shopMerchant, setShopMerchant] = useState('');
    const [showShop, setShowShop] = useState(false);
    useEffect(() => {
        const onEnterShop = (merchantName: string, items: Weapon[]) => {
            if (showShop) return;
            setShowInfoScreen(false);
            setShopMerchant(merchantName);
            setShopItems(Array.from(items));
            setShowShop(true);

            GameAnalytics.addDesignEvent('hud:shop:show');
        };
        gameState.onEnterShop.add(onEnterShop);
        return () => {
            gameState.onEnterShop.remove(onEnterShop);
        };
    }, [showShop]);

    return (
        <MaterialContext.Provider value={props.comp}>
            <Container width="100%" height="100%">
                <Column
                    key="status"
                    position={PositionType.Absolute}
                    top={10}
                    left={10}
                    rowGap={6}
                    maxHeight="5%"
                >
                    <ProgressBar
                        key="healthBar"
                        value={playerHealth / playerMaxHealth}
                        height="100%"
                        minHeight={30}
                        rounding={10}
                        width={420}
                        bgColor={Color.Background}
                        fgColor={
                            playerHealth / playerMaxHealth < 0.25 ? Color.Red : Color.Green
                        }
                    >
                        <Text fontSize={16} padding="2%">
                            {`Health: ${playerHealth} / ${playerMaxHealth}`}
                        </Text>
                    </ProgressBar>
                    <ProgressBar
                        key="energyBar"
                        value={playerEnergy / playerMaxEnergy}
                        height="100%"
                        minHeight={30}
                        rounding={10}
                        width={420}
                        bgColor={Color.Background}
                        fgColor={Color.Primary}
                    >
                        <Text fontSize={16} padding="2%">
                            {`Energy: ${playerEnergy} / ${playerMaxEnergy}`}
                        </Text>
                    </ProgressBar>
                    <Row alignItems={Align.Center} width="100%" columnGap={10}>
                        <Plane
                            height={30}
                            aspectRatio={1.0}
                            material={props.comp.coinMaterial}
                            mesh={props.comp.planeMesh}
                        />
                        <CoinDisplay value={coins} minWidth={100} fontSize={20} />
                        <Text left={10} width={40} fontSize={16}>
                            XP
                        </Text>
                        <Text minWidth={100} fontSize={16}>
                            {displayXp.toString()}
                        </Text>
                        <Text left={10} width={50} fontSize={16}>
                            LVL
                        </Text>
                        <Text minWidth={100} fontSize={16}>
                            {level.toString()}
                        </Text>
                    </Row>
                    {WITH_ADS && !showRespawnScreen && hasRewardedAd && (
                        <Button
                            backgroundColor={Color.Primary}
                            hovered={{
                                backgroundColor: Color.PrimaryHovered,
                            }}
                            active={{
                                backgroundColor: Color.PrimaryHovered,
                            }}
                            width={Constants.adButtonSize + 5 * 2 /* padding */}
                            padding={5}
                            onClick={(e) => {
                                globalAudioManager.playOneShot(Sounds.ButtonPress, {
                                    volume: 0.4,
                                });
                                showRewardedAd(e)
                                    .then((e) => {
                                        const coinReward =
                                            Constants.adCoins +
                                            Constants.adCoinsLvl *
                                                gameState.currentPlayer.level;
                                        gameState.currentPlayer.addCoins(coinReward);

                                        GameAnalytics.addResourceEvent(
                                            ResourceFlowType.Source,
                                            'coins',
                                            coinReward,
                                            'rewarded-ad',
                                            'hud'
                                        );
                                    })
                                    .finally(() => {
                                        setTimeout(() => {
                                            setHasRewardedAd(ads.hasAd());
                                            globalAudioManager.playOneShot(
                                                Sounds.Notification,
                                                {
                                                    volume: 0.3,
                                                }
                                            );
                                        }, Constants.adTimeout * 1000);
                                    });
                                setHasRewardedAd(false);
                            }}
                        >
                            <Plane
                                height={Constants.adButtonSize}
                                aspectRatio={1.0}
                                material={props.comp.adIconMat}
                                mesh={props.comp.planeMesh}
                            />
                        </Button>
                    )}
                </Column>

                {!showShop && (
                    <Column
                        key="messages"
                        position={PositionType.Absolute}
                        right={20}
                        top={10}
                        rowGap={20}
                        width="auto"
                        alignItems={Align.FlexEnd}
                    >
                        {messages.map((m, i) => (
                            <Text key={i.toString()} text={m} fontSize={16} />
                        ))}
                    </Column>
                )}
                {isMobile && !showRespawnScreen && !showShop && (
                    <Column
                        key="mobileCtrls"
                        position={PositionType.Absolute}
                        right={10}
                        bottom={10}
                        rowGap={20}
                    >
                        <Button
                            alignItems={Align.Center}
                            key="btnAttack"
                            width={100}
                            height={100}
                            right={-100}
                            backgroundColor={Color.Primary}
                            hovered={{
                                backgroundColor: Color.PrimaryHovered,
                            }}
                            active={{
                                backgroundColor: Color.PrimaryHovered,
                            }}
                            onClick={(e) => {
                                ControlEvents.onAttackStart.notify();
                            }}
                            paddingTop={10}
                        >
                            <Plane
                                height={80}
                                aspectRatio={1.0}
                                material={props.comp.fightIconMat}
                                mesh={props.comp.planeMesh}
                            />
                        </Button>
                        <Button
                            alignItems={Align.Center}
                            key="btnDash"
                            width={100}
                            height={100}
                            right={-25}
                            backgroundColor={Color.Primary}
                            hovered={{
                                backgroundColor: Color.PrimaryHovered,
                            }}
                            active={{
                                backgroundColor: Color.PrimaryHovered,
                            }}
                            onClick={(e) => {
                                ControlEvents.onDashStart.notify();
                            }}
                            paddingTop={10}
                        >
                            <Plane
                                height={70}
                                aspectRatio={1.0}
                                material={props.comp.dashIconMat}
                                mesh={props.comp.planeMesh}
                            />
                        </Button>
                        <Button
                            alignItems={Align.Center}
                            key="btnPower"
                            width={100}
                            height={100}
                            marginRight={150}
                            backgroundColor={Color.Primary}
                            hovered={{
                                backgroundColor: Color.PrimaryHovered,
                            }}
                            active={{
                                backgroundColor: Color.PrimaryHovered,
                            }}
                            onClick={(e) => {
                                ControlEvents.onPowerStart.notify();
                            }}
                            paddingTop={15}
                        >
                            <Plane
                                height={60}
                                aspectRatio={1.0}
                                material={props.comp.fruitIconMat}
                                mesh={props.comp.planeMesh}
                            />
                        </Button>
                    </Column>
                )}
                <Container
                    position={PositionType.Absolute}
                    width="100%"
                    height="100%"
                    flexDirection={FlexDirection.Column}
                    alignItems={Align.Center}
                    justifyContent={Justify.FlexEnd}
                >
                    <Text fontSize={22} margin={10}>
                        {region}
                    </Text>
                </Container>

                {showShop && (
                    <Container
                        position={PositionType.Absolute}
                        width="100%"
                        height="100%"
                        marginTop={40}
                        flexDirection={FlexDirection.Column}
                        alignItems={Align.Center}
                        justifyContent={Justify.Center}
                    >
                        <Row padding={10}>
                            <Text top={0} left={-20} fontSize={18}>
                                {shopMerchant}
                            </Text>
                        </Row>
                        <Row>
                            <Panel
                                padding={10}
                                width={550}
                                height="auto"
                                mesh={props.comp.planeMesh}
                                material={props.comp.deathScreenBgMat}
                            >
                                {shopItems.map(function (item, i) {
                                    return (
                                        <Row margin={10} key={`btnBuy${i}`}>
                                            <Column width={250} padding={20}>
                                                <Text fontSize={18} text={item.name} />
                                            </Column>
                                            <Column>
                                                <Button
                                                    padding={20}
                                                    backgroundColor={Color.Primary}
                                                    hovered={{
                                                        backgroundColor:
                                                            Color.PrimaryHovered,
                                                    }}
                                                    active={{
                                                        backgroundColor:
                                                            Color.PrimaryHovered,
                                                    }}
                                                    onClick={() => {
                                                        gameState.currentPlayer.buyWeapon(
                                                            item
                                                        );
                                                    }}
                                                >
                                                    <Text
                                                        fontSize={18}
                                                        text={`Buy - ${item.price} Coins`}
                                                    />
                                                </Button>
                                            </Column>
                                        </Row>
                                    );
                                })}
                            </Panel>
                        </Row>
                        {/* <Row height={500}></Row> */}
                    </Container>
                )}
                {showInfoScreen && (
                    <Panel
                        position={PositionType.Absolute}
                        width={330}
                        height={350}
                        bottom={10}
                        left={10}
                        material={props.comp.deathScreenBgMat}
                    >
                        <Row columnGap={10}>
                            <Column>
                                <Plane
                                    position={PositionType.Absolute}
                                    left={-15}
                                    top={15}
                                    width={200}
                                    height={200}
                                    mesh={props.comp.planeMesh}
                                    material={props.comp.keysMat}
                                />
                                <Plane
                                    position={PositionType.Absolute}
                                    left={-17}
                                    top={130}
                                    width={200}
                                    height={200}
                                    mesh={props.comp.planeMesh}
                                    material={props.comp.mouseMat}
                                />
                                <Button
                                    position={PositionType.Absolute}
                                    key="btnCloseInfo"
                                    backgroundColor={Color.Primary}
                                    hovered={{
                                        backgroundColor: Color.PrimaryHovered,
                                    }}
                                    active={{
                                        backgroundColor: Color.PrimaryHovered,
                                    }}
                                    onClick={() => {
                                        setShowInfoScreen(false);
                                        clearTimeout(infoScreenTimout);
                                    }}
                                    top={16}
                                    left={284}
                                    rounding={16}
                                    height={32}
                                    width={32}
                                    padding={8}
                                >
                                    <Text left={1} fontSize={15}>
                                        ×
                                    </Text>
                                </Button>
                            </Column>
                            <Column position={PositionType.Absolute} left={160}>
                                <Text marginTop={50} fontSize={20} height={70}>
                                    Move
                                </Text>
                                <Text marginTop={50} fontSize={20} height={60}>
                                    Fruit Power
                                </Text>
                                <Text marginTop={30} fontSize={20} height={60}>
                                    Dash
                                </Text>
                                <Text marginTop={30} fontSize={20} height={55}>
                                    Attack
                                </Text>
                                <Text marginTop={35} fontSize={20}>
                                    Look
                                </Text>
                            </Column>
                        </Row>
                    </Panel>
                )}
                {!showInfoScreen && (
                    <Panel
                        position={PositionType.Absolute}
                        width={75}
                        height={75}
                        bottom={10}
                        left={10}
                        material={props.comp.deathScreenBgMat}
                        onClick={() => setShowInfoScreen(true)}
                    >
                        <Row columnGap={10}>
                            <Column position={PositionType.Absolute} left={30}>
                                <Text marginTop={18} fontSize={32} height={70}>
                                    ?
                                </Text>
                            </Column>
                        </Row>
                    </Panel>
                )}
                {showRespawnScreen && (
                    <Plane
                        position={PositionType.Absolute}
                        width="100%"
                        height="100%"
                        mesh={props.comp.planeMesh}
                        material={props.comp.deathScreenBgMat}
                        top={0}
                        left={0}
                        flexDirection={FlexDirection.Column}
                        alignItems={Align.Center}
                        justifyContent={Justify.Center}
                        rowGap={15}
                    >
                        <Text fontSize={32}>You died.</Text>
                        <Text fontSize={18}>{deathMessage}</Text>
                        <Row columnGap={10} width="auto">
                            {WITH_ADS && (
                                <Button
                                    key="btnRevive"
                                    backgroundColor={Color.Primary}
                                    hovered={{
                                        backgroundColor: Color.PrimaryHovered,
                                    }}
                                    active={{
                                        backgroundColor: Color.PrimaryHovered,
                                    }}
                                    padding={20}
                                    onClick={(e) => {
                                        showRewardedAd(e).then((e) => {
                                            gameState.currentPlayer.revive();
                                            setShowRespawnScreen(false);
                                        });
                                        setHasRewardedAd(false);
                                        setTimeout(() => {
                                            globalAudioManager.playOneShot(
                                                Sounds.Notification,
                                                {
                                                    volume: 0.2,
                                                }
                                            );
                                            setHasRewardedAd(ads.hasAd());
                                        }, 5000);
                                        GameAnalytics.addDesignEvent('hud:button:revive');
                                    }}
                                >
                                    <Text
                                        key="reviveText"
                                        fontSize={20}
                                        // Nobody knows why this is required
                                        marginRight={20}
                                    >
                                        Watch ad to revive!
                                    </Text>
                                </Button>
                            )}
                            <Button
                                key="btnRespawn"
                                backgroundColor={Color.Primary}
                                hovered={{
                                    backgroundColor: Color.PrimaryHovered,
                                }}
                                active={{
                                    backgroundColor: Color.PrimaryHovered,
                                }}
                                padding={20}
                                onClick={(e) => {
                                    if (respawnTimer <= 0) {
                                        gameState.currentPlayer.respawn();
                                        setShowRespawnScreen(false);
                                        GameAnalytics.addDesignEvent('hud:button:respawn');
                                    }
                                }}
                            >
                                <Text
                                    key="respawnText"
                                    fontSize={20}
                                    // Nobody knows why this is required
                                    marginRight={20}
                                >
                                    {respawnTimer > 0
                                        ? 'Respawn (' + respawnTimer.toString() + ')'
                                        : 'Respawn'}
                                </Text>
                            </Button>
                        </Row>
                        <Row>
                            {isMobile && (
                                <Text
                                    key="socialsText"
                                    fontSize={20}
                                    // Nobody knows why this is required
                                    marginRight={20}
                                    marginTop={20}
                                    marginBottom={20}
                                >
                                    Stay up to date, follow us on:
                                </Text>
                            )}
                        </Row>

                        <Row>
                            <Button
                                material={props.comp.panelSnapchatMat}
                                width={Constants.adButtonSize + 5 * 2 /* padding */}
                                padding={5}
                                marginRight={20}
                                onClick={(e: any) => {
                                    window.open(
                                        'https://www.snapchat.com/add/powerfruitsgame',
                                        '_blank'
                                    );

                                    GameAnalytics.addDesignEvent('hud:button:snapchat');
                                }}
                            >
                                <Plane
                                    height={Constants.adButtonSize}
                                    aspectRatio={1.0}
                                    material={props.comp.snapLogoMat}
                                    mesh={props.comp.planeMesh}
                                />
                            </Button>
                            <Button
                                material={props.comp.panelTikTokMat}
                                width={Constants.adButtonSize + 5 * 2 /* padding */}
                                padding={5}
                                marginRight={20}
                                onClick={(e: any) => {
                                    window.open(
                                        'https://www.tiktok.com/@powerfruitsgame',
                                        '_blank'
                                    );
                                    GameAnalytics.addDesignEvent('hud:button:tiktok');
                                }}
                            >
                                <Plane
                                    height={Constants.adButtonSize}
                                    aspectRatio={1.0}
                                    material={props.comp.tikTokLogoMat}
                                    mesh={props.comp.planeMesh}
                                />
                            </Button>
                        </Row>
                    </Plane>
                )}
            </Container>
        </MaterialContext.Provider>
    );
};

/**
 * react-ui
 */
export class ReactUi extends ReactUiBase {
    static TypeName = 'hud';
    static InheritProperties = true;

    @property.material({required: true})
    coinMaterial!: Material;

    @property.material({required: true})
    deathScreenBgMat!: Material;

    @property.material({required: true})
    keysMat!: Material;

    @property.material({required: true})
    mouseMat!: Material;

    @property.material({required: true})
    dashIconMat!: Material;
    @property.material({required: true})
    fightIconMat!: Material;
    @property.material({required: true})
    fruitIconMat!: Material;
    @property.material({required: true})
    adIconMat!: Material;
    @property.material({required: true})
    tikTokLogoMat!: Material;
    @property.material({required: true})
    snapLogoMat!: Material;

    @property.mesh({required: true})
    planeMesh!: Mesh;

    @property.object({required: true})
    camera!: Object3D;

    render() {
        return <App comp={this} />;
    }

    panelTikTokMat: Material;
    panelSnapchatMat: Material;

    start() {
        super.start();
        /* After pointer lock (which is currently disabled because webgl context losses),
         * the up event is only fired on the window, not on the element.
         * The function currently only disables camera rotation */
        window.addEventListener('pointerup', this.exitPointerLock.bind(this));
        window.addEventListener('contextmenu', function (event) {
            event.preventDefault();
        });

        this.panelMaterial.setColor(Color.Primary);

        this.panelTikTokMat = this.panelMaterial.clone()!;
        this.panelTikTokMat.setColor([0, 0, 0, 1.0]);
        this.panelSnapchatMat = this.panelMaterial.clone()!;
        this.panelSnapchatMat.setColor([1.0, 252 / 255, 0, 1.0]);
    }

    override onPointerDown(event: PointerEvent) {
        let t = super.onPointerDown(event);

        let hasEvent = false;
        while (t && !hasEvent) {
            hasEvent = !!t?.props.onClick || !!t?.props.onDown || !!t?.props.onUp;
            console.log(hasEvent, t);
            t = t.parent ?? null;
        }

        /* Only turn camera, if the event is not handled by the UI */
        if (!hasEvent) {
            ControlEvents.onPointerDown.notify(event);

            this.camera.getComponent(ThirdpersonCamera).allowRotation = true;

            /* We'd love to pointer-lock here, but it causes WebGL context losses
            const element = document.body;
            element.requestPointerLock =
                element.requestPointerLock ||
                (element as any).mozRequestPointerLock ||
                (element as any).webkitRequestPointerLock;

            element.requestPointerLock();
             */
        }

        return t;
    }

    override onPointerUp(event: PointerEvent) {
        let t = super.onPointerUp(event);

        let hasEvent = false;
        while (t && !hasEvent) {
            hasEvent = !!t?.props.onClick || !!t?.props.onDown || !!t?.props.onUp;
            console.log(hasEvent, t);
            t = t.parent ?? null;
        }
        /* UI takes precendence */
        if (!hasEvent) ControlEvents.onPointerUp.notify(event);
        return t;
    }

    exitPointerLock(event: PointerEvent) {
        this.camera.getComponent(ThirdpersonCamera).allowRotation = false;

        /* Unlock the mouse if its locked, but currently disabled since pointer locking causes WebGL context loss
        if (document.pointerLockElement) {
            document.exitPointerLock =
                document.exitPointerLock ||
                (document as any).mozExitPointerLock ||
                (document as any).webkitExitPointerLock;
            document.exitPointerLock();
        }
         */
    }
}
